import { CheckCircle2, CircleAlert, TriangleAlert } from "lucide-mui";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { type ReactNode, useState } from "react";
import ConfirmDialog from "./ConfirmDialog.tsx";
import FormattedDate from "./FormattedDate.tsx";

// The field and its button sit on one line; the button takes a small field's height.

export interface SecretTestResult {
  ok: boolean;
  detail: ReactNode;
}

interface SecretCredentialFormProps {
  /** What the secret is: "Claude session token", "API key". */
  name: ReactNode;
  description?: ReactNode;
  stored: boolean;
  /** The stored secret's last characters, shown as "ending in …hint". */
  hint?: string | null;
  /** When it last changed and by whom; leave out to hide the line. */
  updatedAt?: string | null;
  updatedByEmail?: string | null;
  /** "Last changed" by default; "Last rotated" for a token. */
  changedLabel?: string;
  /** Without an encryption key on the deployment, saving a secret is refused. */
  encryptionConfigured: boolean;
  /** Finishes "SECRET_ENCRYPTION_KEY is not set on this deployment, so …". */
  encryptionWarning?: ReactNode;
  /** May set, test and clear it; otherwise the form only says what is stored. */
  canManage?: boolean;

  /** The write-only field. */
  value: string;
  onChange: (value: string) => void;
  fieldLabel: string;
  placeholder?: string;
  helperText?: ReactNode;
  /** A save button next to the field; leave out when a surrounding form saves the secret. */
  onSave?: () => void;
  saving?: boolean;
  saveLabel?: string;
  saveDisabled?: boolean;

  onTest?: () => void;
  testing?: boolean;
  testDisabled?: boolean;
  testLabel?: string;
  testResult?: SecretTestResult | null;
  /** Lets the test result be closed. */
  onDismissTestResult?: () => void;

  /** Offered while a secret is stored, behind a confirm that spins until a returned promise settles. */
  onClear?: () => unknown;
  clearLabel?: string;
  clearConfirm?: { title: string; body?: ReactNode; confirmLabel?: string };

  /** State specific to this credential (a fallback in use, runs failing), above the field. */
  children?: ReactNode;
}

/**
 * A stored secret (API key, session token): which one is stored and when it
 * changed, Test and Clear, the encryption guard, and a write-only field to set
 * a new one. The secret itself is never shown again.
 */
export default function SecretCredentialForm({
  name,
  description,
  stored,
  hint,
  updatedAt,
  updatedByEmail,
  changedLabel = "Last changed",
  encryptionConfigured,
  encryptionWarning = "saving is refused rather than storing it in clear text.",
  canManage = true,
  value,
  onChange,
  fieldLabel,
  placeholder,
  helperText,
  onSave,
  saving = false,
  saveLabel = "Save",
  saveDisabled = false,
  onTest,
  testing = false,
  testDisabled = false,
  testLabel = "Test",
  testResult,
  onDismissTestResult,
  onClear,
  clearLabel = "Clear",
  clearConfirm,
  children,
}: SecretCredentialFormProps) {
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);
  const canClear = stored && !!onClear;

  const clear = async () => {
    if (!onClear) return;
    setClearing(true);
    try {
      await onClear();
      setConfirmClear(false);
    } catch {
      // The mutation's own error toast says what went wrong; keep the dialog open.
    } finally {
      setClearing(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Stack
        direction="row"
        spacing={2}
        sx={{ alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", rowGap: 1 }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="body1">
            {name}
            {stored && hint && (
              <>
                {" "}
                ending in <strong>…{hint}</strong>
              </>
            )}
          </Typography>
          {description && (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              {description}
            </Typography>
          )}
          {updatedAt && (
            <Typography variant="caption" sx={{ color: "text.secondary" }}>
              {changedLabel} <FormattedDate value={updatedAt} format="datetime" />
              {updatedByEmail ? ` by ${updatedByEmail}` : ""}
            </Typography>
          )}
        </Box>
        {canManage && (onTest || canClear) && (
          <Stack direction="row" spacing={1}>
            {onTest && (
              <Button variant="outlined" onClick={onTest} loading={testing} disabled={testDisabled}>
                {testLabel}
              </Button>
            )}
            {canClear && (
              <Button variant="outlined" color="error" onClick={() => setConfirmClear(true)}>
                {clearLabel}
              </Button>
            )}
          </Stack>
        )}
      </Stack>

      {testResult && (
        <Alert
          severity={testResult.ok ? "success" : "error"}
          icon={testResult.ok ? <CheckCircle2 /> : <CircleAlert />}
          onClose={onDismissTestResult}
        >
          {testResult.detail}
        </Alert>
      )}

      {!encryptionConfigured && (
        <Alert severity="warning" icon={<TriangleAlert />}>
          <code>SECRET_ENCRYPTION_KEY</code> is not set on this deployment, so {encryptionWarning}
        </Alert>
      )}

      {children}

      {canManage && (
        <Stack direction="row" spacing={1} sx={{ alignItems: "flex-start" }}>
          <TextField
            label={fieldLabel}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            type="password"
            size="small"
            fullWidth
            autoComplete="off"
            placeholder={placeholder}
            helperText={helperText}
          />
          {onSave && (
            <Button
              variant="contained"
              onClick={onSave}
              loading={saving}
              disabled={saveDisabled}
              sx={{ flexShrink: 0 }}
            >
              {saveLabel}
            </Button>
          )}
        </Stack>
      )}

      <ConfirmDialog
        open={confirmClear}
        title={clearConfirm?.title ?? "Clear the stored secret"}
        confirmLabel={clearConfirm?.confirmLabel ?? clearLabel}
        pending={clearing}
        onConfirm={() => void clear()}
        onClose={() => setConfirmClear(false)}
      >
        {clearConfirm?.body}
      </ConfirmDialog>
    </Stack>
  );
}
